import { Controller } from "@hotwired/stimulus"
import { DisplaySurveyType, type PostHog } from "posthog-js"

import { getPostHogReady } from "@/lib/posthog"

// Connects to data-controller="help-button"
// Default survey ID (can be overridden via Stimulus value from ENV)
const DEFAULT_SURVEY_ID = "019bd8c0-7092-0000-5d66-64b5431934e9"
const SURVEY_LOAD_TIMEOUT_MS = 5000

export default class HelpButtonController extends Controller {
  static values = {
    surveyId: { type: String, default: "" },
  }

  declare surveyIdValue: string
  private timeoutId: ReturnType<typeof setTimeout> | null = null
  private isDisconnected = false
  private isLoading = false
  private promiseReject: ((error: Error) => void) | null = null

  disconnect() {
    this.isLoading = false
    this.isDisconnected = true
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
    // Reject any pending promise to prevent it from hanging
    if (this.promiseReject) {
      this.promiseReject(new Error("Controller disconnected"))
      this.promiseReject = null
    }
  }

  async handleClick(event: Event) {
    event.preventDefault()

    // Prevent concurrent clicks from creating orphaned timeouts/promises
    if (this.isLoading) {
      return
    }
    this.isLoading = true

    const surveyId = this.surveyIdValue || DEFAULT_SURVEY_ID

    try {
      const posthog = await getPostHogReady()

      // Check if still connected after async operations
      if (!this.element.isConnected) {
        return
      }

      await this.waitForSurveysLoaded(posthog)

      if (!this.element.isConnected) {
        return
      }

      this.displaySurvey(posthog, surveyId)
    } catch (error) {
      // Don't redirect if controller was disconnected (user navigated away)
      if (
        error instanceof Error &&
        error.message === "Controller disconnected"
      ) {
        return
      }
      console.error("Help button: Error displaying survey:", error)
      window.location.href = "/contact"
    } finally {
      this.isLoading = false
    }
  }

  // Wait for PostHog surveys to be loaded
  // Per PostHog docs: "Surveys are not immediately available on page load"
  private waitForSurveysLoaded(posthog: PostHog): Promise<void> {
    return new Promise((resolve, reject) => {
      this.promiseReject = reject

      // Timeout fallback to prevent hanging indefinitely
      this.timeoutId = setTimeout(() => {
        if (!this.isDisconnected && this.promiseReject) {
          this.promiseReject = null
          reject(new Error("Surveys did not load within timeout"))
        }
      }, SURVEY_LOAD_TIMEOUT_MS)

      // PostHog doesn't provide an API to unregister callbacks.
      // The callback is guarded by isDisconnected to prevent execution after disconnect.
      posthog.onSurveysLoaded(() => {
        if (this.isDisconnected) {
          return
        }

        if (this.timeoutId) {
          clearTimeout(this.timeoutId)
          this.timeoutId = null
        }
        this.promiseReject = null
        resolve()
      })
    })
  }

  private displaySurvey(posthog: PostHog, surveyId: string) {
    // Workaround for PostHog Bug #2586: Survey fails to display on second attempt
    // after being closed because the old DOM element isn't cleaned up properly.
    // See: https://github.com/PostHog/posthog-js/issues/2586
    document.querySelector(`.PostHogSurvey-${surveyId}`)?.remove()

    posthog.displaySurvey(surveyId, {
      displayType: DisplaySurveyType.Popover,
      ignoreConditions: true,
      ignoreDelay: true,
    })
  }
}
